import React, { useState } from "react";
import { Button, Form, DropdownButton, Dropdown } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody, Row, Col, Table } from "reactstrap";
import { Loading } from "./Loading";
import DataTable from "react-data-table-component";
import ServiceCalls from "../utils/serviceCalls.js";
/*import RoleUtils from "../utils/roleUtils";*/
import CommonUtils from "../utils/siteUtils";
import { TableRoleDisplay } from "./TableRoleDisplay";
/*import * as XLSX from "xlsx/xlsx.mjs";*/
import * as XLSX from "@e965/xlsx/xlsx.mjs";
import "../styles/Admin.css";
import "../styles/PopUps.css";

/**
 * Admin portal access search components
 * AdminAccessSearch - main component
 * AdminAccessSearchTable - table component
 * AdminAccessViewDetails - modal component
 */
export const AdminAccessSearch = (props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [searchString, setSearchString] = useState("");
	const [searchResults, setSearchResults] = useState(null);
	const [isValid, setIsValid] = useState(true);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedData, setSelectedData] = useState(null);
	const [hasSearched, setHasSearched] = useState(false);

	//Function to open the details modal and set the selected data to the row data
	const openDetails = (data) => {
		setSelectedData(data);
		setDetailsOpen(true);
	};

	//Function to close the details modal and clear the selected data
	const closeDetails = () => {
		setDetailsOpen(false);
		setSelectedData(null);
	};

	const onClearForm = () => {
		setSearchString("");
		setIsValid(true);
		setHasSearched(false);
		setSearchResults(null);
	};

	//Function to handle the search - hits the API/server and sets the search results
	const handleSearch = async () => {
		if (searchString && formValidate(searchString)) {
			props.onChildUpdate();
			setIsLoading(true);
			setIsValid(true);
			setHasSearched(true);
			ServiceCalls.getAdminAccessResults(searchString)
				.then(function (data) {
					if (data) {
						setSearchResults(data);
					}
					setIsLoading(false);
				})
				.catch((error) => {
					console.error("Error occurred while fetching admin access results:", error);
				});
		} else {
			setIsValid(false);
		}
	};

	//Function to validate the search input with regex
	const formValidate = (input) => {
		var regex = /^[^<>"()]*$/  //DE1270974 - Allow characters that are allowed in email field
		return regex.test(input);
	};

	//Function to prepare the data for export to excel/csv
	const prepForExport = () => {
		const uniqueEntries = new Set();
		const dataForExport = searchResults.flatMap((row) => {
			if (row.suppliers && row.suppliers.length > 0) {
				return row.suppliers.flatMap((supplier) =>
					supplier.contactRoles.flatMap((role) => {
						// Create a unique identifier for the contact-supplier-role combination 
						//DE841497 Not Exporting All Data
						const uniqueId = `${row.contactId}-${supplier.supplierName}-${role.displayName}`;
						// If this uniqueId has already been encountered, skip this entry
						if (uniqueEntries.has(uniqueId)) {
							return [];
						} else {
							uniqueEntries.add(uniqueId);
							return [
								{
									"Contact Name": (row.firstName || "") + " " + (row.lastName || ""),
									"Contact Email": row.email,
									"Supplier Name": supplier.supplierName,
									"Last Signed On": row.lastSignOnDate ? CommonUtils.formatDate(row.lastSignOnDate, true) : "",
									"Security Role(s)": role.displayName.split(',')[0].trim(),
									Status: role.enabled ? "Active" : "Inactive",
								},
							];
						}
					})
				);
			} else {
				// If there are no suppliers, return a single default entry
				return [
					{
						"Contact Name": (row.firstName || "") + " " + (row.lastName || ""),
						"Contact Email": row.email,
						"Supplier Name": "",
						"Last Signed On": row.lastSignOnDate ? CommonUtils.formatDate(row.lastSignOnDate, true) : "",
						"Security Role(s)": "",
						Status: "",
					},
				];
			}
		});

		// Sort the data by supplier name, case-insensitive
		dataForExport.sort((a, b) => a["Supplier Name"].toLowerCase().localeCompare(b["Supplier Name"].toLowerCase()));
		return dataForExport;
	};

	//Function to export the search results to excel
	const exportExcelResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Admin Access Search Results");
		XLSX.writeFile(wb, "AdminAccessSearchResults.xlsx");
	};

	//Function to export the search results to csv
	const exportCSVResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const csv = XLSX.utils.sheet_to_csv(ws);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "AdminAccessSearchResults.csv";
		link.click();
	};

	return (
		<div className="accessSearchContainer">
			<div id="accessSearchArea" className="flex-column borderCurve">
				<div className="accessSearchHeader">
					<h3>Access Search</h3>
					{isValid ? <></> : <span className="invalid">&nbsp;&nbsp;Invalid input</span>}
				</div>

				<Form
					className="d-flex accessSearchBar"
					onSubmit={(e) => {
						e.preventDefault();
						handleSearch();
					}}>
					<Form.Control type="text" placeholder="Enter contact email, last name or role name" className="mr-sm-2 flex-grow-1" onChange={(e) => setSearchString(e.target.value)} value={searchString} />
					<div className="d-flex justify-content-end">
						<Button variant="secondary" className="mr-2 align-top adminAccessSearchButton" onClick={onClearForm} style={{ marginRight: "5px" }}>
							Clear
						</Button>
						<Button variant="primary" type="submit" className="mr-2 align-top adminAccessSearchButton">
							Search
						</Button>
					</div>
					{searchResults && searchResults.length > 0 && !isLoading ? (
						<DropdownButton title="Export Results" id="bg-nested-dropdown" variant="primary">
							<Dropdown.Item eventKey="1" onClick={exportCSVResults}>
								CSV
							</Dropdown.Item>
							<Dropdown.Item eventKey="2" onClick={exportExcelResults}>
								Excel
							</Dropdown.Item>
						</DropdownButton>
					) : (
						<></>
					)}
				</Form>
				{isLoading && <Loading />}
				{!isLoading && hasSearched && (searchResults && searchResults.length > 0 ? <AdminAccessSearchTable data={searchResults} openDetails={openDetails} /> : <div>No results found</div>)}
				{selectedData && <AdminAccessViewDetails data={selectedData} open={detailsOpen} toggle={closeDetails} />}
			</div>
		</div>
	);
};

const AdminAccessSearchTable = ({ data, openDetails }) => {
	// First, collect all locked/disabled roles for each contact
	const contactLockedDisabledRoles = {};
	//US9778761 Fixed an issue of locked/disabled roles added to wrong contacts of the search results by
	//filtering locked / disabled roles and adding them to to all suppliers of that contact
	data.forEach((row) => {
		if (row.suppliers && row.suppliers.length > 0) {
			const lockedDisabledRoles = [];

			// Find all locked (roleId 6) and disabled (roleId 7) roles for this contact
			row.suppliers.forEach((supplier) => {
				supplier.contactRoles.forEach((role) => {
					if (role.roleId === 6 || role.roleId === 7) {
						// Check if we don't already have this role
						if (!lockedDisabledRoles.some(r => r.roleId === role.roleId)) {
							lockedDisabledRoles.push(role);
						}
					}
				});
			});

			// Store locked/disabled roles for this contact
			if (lockedDisabledRoles.length > 0) {
				contactLockedDisabledRoles[row.contactId] = lockedDisabledRoles;
			}
		}
	});

	// First, we flatten the data structure. Each contact may have multiple suppliers, and each supplier may have multiple roles.
	// We want to create a new array where each element represents a unique combination of contact and supplier, including all the roles for that supplier.

	const dataWithSuppliers = data
		.flatMap((row) => {
			// Check if the contact has any suppliers. If not, we return an empty array to skip this contact.
			return row.suppliers && row.suppliers.length > 0
				? row.suppliers.map((supplier) => ({
					// Spread the contact details into the new object.
					...row,
					// Add supplier-specific details to the object.
					supplierName: supplier.supplierName,
					contactRoles: supplier.contactRoles,
					supplierIndex: supplier.supplierId,
				}))
				: []; // If there are no suppliers, return an empty array for this iteration.
		})
		.reduce((acc, curr) => {
			// 'acc' (accumulator) will be our final array of unique contact-supplier combinations with aggregated roles.
			// 'curr' (current) is the current contact-supplier object we are looking to add or merge into the accumulator.

			// Attempt to find an existing entry in the accumulator that matches the current contact ID and supplier ID.
			const foundIndex = acc.findIndex((item) => item.supplierIndex === curr.supplierIndex && item.contactId === curr.contactId);

			//Add Admin roles to ALL suppliers 
			if (contactLockedDisabledRoles[curr.contactId]) {
				contactLockedDisabledRoles[curr.contactId].forEach((lockedDisabledRole) => {
					// Check if this supplier already has this role
					if (curr.contactRoles.findIndex((extItem)=>extItem.roleId=== lockedDisabledRole.roleId) ===-1) {
						curr.contactRoles.push(lockedDisabledRole);
					}
				});
			}
			if (foundIndex > -1) {

				curr.contactRoles.forEach((role) => {
					const existingRoles = acc[foundIndex].contactRoles;

					// Find an existing role with the same description
					const existingRoleIndex = existingRoles.findIndex((existingRole) => existingRole.description === role.description);

					// If there is an existing role with the same description
					if (existingRoleIndex !== -1) {
						// Check if the current role is enabled and the existing one is not
						if (role.enabled && !existingRoles[existingRoleIndex].enabled) {
							// Replace the existing role with the current one
							existingRoles[existingRoleIndex] = role;
						}
						// If the current role is not enabled, or both are enabled, do nothing
					} else {
						// If there is no existing role with the same description, add the current role
						existingRoles.push(role);
					}
				});
			} else {
				//Prevent Admin Suppliers from being added to the list unless user is DISABLED (has no other roles)
				if (curr.suppliers && (curr.suppliers[0].vendorId !== '00INACTIVE' ||
					(curr.suppliers[0].vendorId === '00INACTIVE' && curr.suppliers[0].contactRoles[0].roleId === 7))) {
					acc.push(curr);
				}
			}

			return acc;
		}, []);

	//Table columns
	const columns = [
		{
			name: "Contact Name",
			selector: (row) => (row.firstName || "") + " " + (row.lastName || ""),
			sortable: true,
			maxwidth: "200px",
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => (row.firstName || "") + " " + (row.lastName || "")),
		},
		{
			name: "Contact Email",
			selector: (row) => row.email,
			sortable: true,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.email),
		},
		{
			name: "Supplier Name",
			selector: (row) => row.supplierName,
			sortable: true,
			id: "supplierName",
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.supplierName),
		},
		{
			name: "Last Signed On",
			selector: (row) => (row.lastSignOnDate ? new Date(CommonUtils.formatDate(row.lastSignOnDate, true)).getTime() : ""),
			sortable: true,
			maxwidth: "200px",
			format: (row) => (row.lastSignOnDate ? CommonUtils.formatDate(row.lastSignOnDate, true) : ""),
		},
		{
			name: "Security Role(s)",
			cell: (row) => {
				// DE839068: Reorder Role Names
				const roles = (row.contactRoles)?row.contactRoles.map((role) => ({ roleId: role.roleId, description: role.description, enabled: role.enabled })):[];
				return (
					<div style={{ marginTop: "10px" }}>
						<TableRoleDisplay roles={roles} showInactiveRoles={true} />
					</div>
				);
			},
			maxwidth: "300px",
		},
		{
			name: "",
			button: "true",
			cell: (row) => (
				<Button
					variant="primary"
					style={{ padding: "5px" }}
					onClick={() => {
						// DEFECT FIX - Search on Admin will return same user based on
						// supplierIndex, switch to ContactId AND supplierIndex
						//const selectedSupplier = dataWithSuppliers.find((supplier) => supplier.supplierIndex === row.supplierIndex);
						const selectedSupplier = dataWithSuppliers.find((supplier) => (supplier.contactId === row.contactId && supplier.supplierIndex === row.supplierIndex));
						openDetails(selectedSupplier);
					}}>
					View Details
				</Button>
			),
		},
	];

	//Custom styles for the table
	const customStyles = {
		table: {
			style: {
				padding: "0",
				margin: "0",
			},
		},
		responsiveWrapper: {
			style: {
				borderRadius: "unset",
			},
		},
		headCells: {
			style: {
				fontSize: "14px",
				justifyContent: "left !important",
				color: "white",
				paddingLeft: "1%",
				borderRight: "1px solid white",
				fontWeight: "400",
			},
		},
		head: {
			style: {
				zIndex: "99",
			},
		},
		headRow: {
			style: {
				backgroundColor: "#003C71",
			},
		},
	};
	return (
		<>
			<div className="searchResultsCnt" style={{ float: "right", marginTop: "unset" }}>
				<span className="searchResultsPill">{dataWithSuppliers.length}</span> Results
			</div>
			<DataTable columns={columns} data={dataWithSuppliers} pagination striped responsive fixedHeader customStyles={customStyles} defaultSortFieldId="supplierName" defaultSortAsc={true} />
		</>
	);
};

const AdminAccessViewDetails = ({ data, open, toggle }) => {
	const { firstName, lastName, email, contactId, lastSignOnDate, ohcidRegistration, suppliers } = data;
	const [supplier] = suppliers;
	const { statusId, create_Dt, registered_Dt, /*inviteExpire_Dt*/ } = ohcidRegistration;
	const { supplierId, supplierName, vendorId, vendorSetId } = supplier;
	//US7652820 - Disable Roles if user has locked account
	var hasLockedAccount = data.contactRoles.some(r => r.roleId === 6);
	// Sort roles with SPUA on top, assuming SPUA has roleId of 3
	// 	DE839068: Reorder Role Names
	const sortedRoles = data.contactRoles.slice().sort((a, b) => {
		if (!hasLockedAccount) {
			if (a.roleId === 3) return -1;
			if (b.roleId === 3) return 1;
		}
		return a.description.localeCompare(b.description);
	});

	return (
		<Modal centered={true} fade={false} isOpen={open} toggle={toggle} size="xl">
			<ModalHeader toggle={toggle} className="view-request-header" close={<></>}>
				Access Details
			</ModalHeader>
			<ModalBody>
				<Form>
					<Row>
						<Col sm={6}>
							<h4 className="modal-subheader">Contact</h4>
							<Row>
								<Col sm={4}>
									<strong>Contact Name</strong>
								</Col>
								<Col sm={8}>
									{firstName} {lastName}
								</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Contact Email</strong>
								</Col>
								<Col sm={8} className="accessSearch-break-word">{email}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Contact ID</strong>
								</Col>
								<Col sm={8}>{contactId}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Last Sign On Date</strong>
								</Col>
								<Col sm={8}>{lastSignOnDate ? CommonUtils.formatDate(lastSignOnDate, true) : null}</Col>
							</Row>
						</Col>
						<Col sm={6}>
							<h4 className="modal-subheader">Registration</h4>
							<Row>
								<Col sm={4}>
									<strong>Status</strong>
								</Col>
								<Col sm={8}>{CommonUtils.determineRegistrationStatus(statusId)}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Created Date</strong>
								</Col>
								<Col sm={8}>{create_Dt ? CommonUtils.formatDate(create_Dt) : null}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Registered Date</strong>
								</Col>
								<Col sm={8}>{registered_Dt ? CommonUtils.formatDate(registered_Dt) : null}</Col>
							</Row>
						</Col>
					</Row>
					<hr />
					<Row>
						<Col sm={6}>
							<h4 className="modal-subheader">Supplier</h4>
							<Row>
								<Col sm={4}>
									<strong>Supplier Name</strong>
								</Col>
								<Col sm={8}>{supplierName}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Supplier ID</strong>
								</Col>
								<Col sm={8}>{supplierId}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Vendor ID</strong>
								</Col>
								<Col sm={8}>{vendorId}</Col>
							</Row>
							<Row>
								<Col sm={4}>
									<strong>Vendor Set ID</strong>
								</Col>
								<Col sm={8}>{vendorSetId}</Col>
							</Row>
						</Col>
						<Col sm={6}>
							<Table borderless className="viewDetails-roles-table" size="sm">
								<thead>
									<tr>
										<th>Role</th>
										<th>Status Date</th>
										<th>Notes</th>
									</tr>
								</thead>
								<tbody>
									{sortedRoles.map((role, index) => (
										<tr key={`${role.roleId}-${index}`}>
											<td>
												<TableRoleDisplay roles={[role]} showInactiveRoles={true} />
											</td>
											<td>{role.updatedDt && CommonUtils.formatDate(role.updatedDt)}</td>
											<td>{role.statusNotes}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Col>
					</Row>
					<Button className="request-details-popup-buttons" onClick={() => toggle()}>
						Close
					</Button>
				</Form>
			</ModalBody>
		</Modal>
	);
};
